import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, 
  Loader2,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Download
} from 'lucide-react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface ContratoComComissao {
  id: string;
  nome_cliente: string;
  valor_total: number;
  comissao_parceiro_valor: number;
  status: string;
  created_at: string;
  is_rede?: boolean;
  tipo_contrato?: 'fotovoltaico' | 'carregador';
  pagamentos: Pagamento[];
}

interface Pagamento {
  id: string;
  valor_pago: number;
  data_pagamento: string;
  forma_pagamento: string;
  observacoes?: string;
  comprovante_url?: string;
  comprovante_nome_arquivo?: string;
  origem?: 'comissao' | 'financeiro';
}

interface ComissaoCarregadores {
  total_comissoes: number;
  total_pago: number;
  saldo: number;
  pagamentos: Pagamento[];
}

interface ParceiroFinanceiroTabProps {
  parceiroId: string;
}

const FORMAS_PAGAMENTO = [
  { value: 'pix', label: 'PIX' },
  { value: 'transferencia', label: 'Transferência Bancária' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'outros', label: 'Outros' },
];

export function ParceiroFinanceiroTab({ parceiroId }: ParceiroFinanceiroTabProps) {
  const [contratos, setContratos] = useState<ContratoComComissao[]>([]);
  const [contratosCarregadores, setContratosCarregadores] = useState<ContratoComComissao[]>([]);
  const [comissaoCarregadores, setComissaoCarregadores] = useState<ComissaoCarregadores | null>(null);
  const [comissaoCarregadoresParceiroPai, setComissaoCarregadoresParceiroPai] = useState<ComissaoCarregadores | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (parceiroId) {
      fetchContratos();
    }
  }, [parceiroId]);

  const fetchContratos = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/financeiro`);

      if (!response.ok) {
        throw new Error('Erro ao carregar dados financeiros');
      }

      const data = await response.json();
      setContratos(data.contratos || []);
      setContratosCarregadores(data.contratosCarregadores || []);
      setComissaoCarregadores(data.comissaoCarregadores ?? null);
      setComissaoCarregadoresParceiroPai(data.comissaoCarregadoresParceiroPai ?? null);
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const calcularTotalComissao = () => {
    return contratos.reduce((total, contrato) => total + (contrato.comissao_parceiro_valor || 0), 0);
  };


  const calcularSaldoContrato = (contrato: ContratoComComissao) => {
    const totalPago = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
    return contrato.comissao_parceiro_valor - totalPago;
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      'rascunho': { label: 'Rascunho', variant: 'secondary' },
      'proposta_aceita': { label: 'Proposta Aceita', variant: 'outline' },
      'contrato_gerado': { label: 'Contrato Gerado', variant: 'default' },
      'em_andamento': { label: 'Em andamento', variant: 'default' },
      'assinado': { label: 'Assinado', variant: 'default' },
    };
    const statusInfo = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const totalComissaoFotovoltaico = calcularTotalComissao();
  const totalComissaoCarregadores = (comissaoCarregadores?.total_comissoes ?? 0) + (comissaoCarregadoresParceiroPai?.total_comissoes ?? 0);
  const totalComissao = totalComissaoFotovoltaico + totalComissaoCarregadores;

  const todosContratos = useMemo(() => {
    const lista = [
      ...contratos.map(c => ({ ...c, tipo_contrato: (c.tipo_contrato ?? 'fotovoltaico') as 'fotovoltaico' | 'carregador' })),
      ...contratosCarregadores.map(c => ({ ...c, tipo_contrato: 'carregador' as const }))
    ];
    return lista.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [contratos, contratosCarregadores]);

  // Calcula total pago baseado apenas nos contratos exibidos (mesma base da tabela e histórico)
  const totalPago = useMemo(() => {
    return todosContratos.reduce((total, contrato) => {
      const pagamentosContrato = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
      return total + pagamentosContrato;
    }, 0);
  }, [todosContratos]);

  const saldoDevedor = totalComissao - totalPago;

  // Histórico unificado: lista de contratos com seus pagamentos, ordenado por data do último pagamento
  const historicoUnificado = useMemo(() => {
    type Grupo = { titulo: string; subtitulo: string; tipoProduto?: 'fotovoltaico' | 'carregador'; pagamentos: Pagamento[] };
    const gruposMap = new Map<string, Grupo>();
    const idsIncluidos = new Set<string>();

    const addPagamento = (key: string, titulo: string, subtitulo: string, pag: Pagamento, tipoProduto?: 'fotovoltaico' | 'carregador') => {
      if (!gruposMap.has(key)) gruposMap.set(key, { titulo, subtitulo, tipoProduto, pagamentos: [] });
      gruposMap.get(key)!.pagamentos.push(pag);
      idsIncluidos.add(String(pag.id));
    };

    todosContratos.forEach((contrato) => {
      contrato.pagamentos?.forEach((pag) => {
        addPagamento(
          contrato.id,
          contrato.nome_cliente,
          `Contrato de ${formatCurrency(contrato.valor_total)}`,
          pag,
          contrato.tipo_contrato
        );
      });
    });

    return Array.from(gruposMap.values()).sort((a, b) => {
      const dataA = Math.max(...a.pagamentos.map((p) => new Date(p.data_pagamento).getTime()));
      const dataB = Math.max(...b.pagamentos.map((p) => new Date(p.data_pagamento).getTime()));
      return dataB - dataA;
    });
  }, [todosContratos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo Financeiro */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Comissões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(totalComissao)}
              </div>
              <DollarSign className="h-8 w-8 text-blue-600/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(totalPago)}
              </div>
              <TrendingUp className="h-8 w-8 text-green-600/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Saldo Devedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className={`text-2xl font-bold ${saldoDevedor > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(saldoDevedor)}
              </div>
              <TrendingDown className={`h-8 w-8 ${saldoDevedor > 0 ? 'text-red-600/20' : 'text-green-600/20'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Contratos */}
      <Card>
        <CardHeader>
          <CardTitle>Contratos e Comissões</CardTitle>
        </CardHeader>
        <CardContent>
          {todosContratos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum contrato encontrado</h3>
              <p className="text-muted-foreground text-center">
                Este parceiro ainda não possui contratos fechados com indicação (fotovoltaico ou carregadores veiculares).
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Data do Contrato</TableHead>
                    <TableHead>Valor do Contrato</TableHead>
                    <TableHead>Comissão Devida</TableHead>
                    <TableHead>Total Pago</TableHead>
                    <TableHead>Saldo</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {todosContratos.map((contrato) => {
                    const totalPagoContrato = contrato.pagamentos?.reduce((sum, pag) => sum + pag.valor_pago, 0) || 0;
                    const saldoContrato = calcularSaldoContrato(contrato);
                    const isCarregador = contrato.tipo_contrato === 'carregador';
                    const isFotovoltaico = contrato.tipo_contrato === 'fotovoltaico';

                    return (
                      <TableRow key={contrato.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col gap-1">
                            <span>{contrato.nome_cliente}</span>
                            <div className="flex flex-wrap gap-1">
                              {contrato.is_rede && (
                                <Badge variant="outline" className="w-fit text-[10px] h-4 bg-blue-50 text-blue-700 border-blue-200">
                                  Rede
                                </Badge>
                              )}
                              {isFotovoltaico && (
                                <Badge variant="outline" className="w-fit text-[10px] h-4 bg-amber-50 text-amber-700 border-amber-200">
                                  Fotovoltaico
                                </Badge>
                              )}
                              {isCarregador && (
                                <Badge variant="secondary" className="w-fit text-[10px] h-4">
                                  Carregador
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(contrato.created_at)}</TableCell>
                        <TableCell>{formatCurrency(contrato.valor_total)}</TableCell>
                        <TableCell className="font-semibold text-blue-600">
                          {formatCurrency(contrato.comissao_parceiro_valor)}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {formatCurrency(totalPagoContrato)}
                        </TableCell>
                        <TableCell className={`font-semibold ${saldoContrato > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(saldoContrato)}
                        </TableCell>
                        <TableCell>{getStatusBadge(contrato.status)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Pagamentos unificado (fotovoltaico + carregadores no mesmo padrão) */}
      {historicoUnificado.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Pagamentos</CardTitle>
            <p className="text-sm text-muted-foreground">
              Pagamentos de comissão (contratos fotovoltaicos e carregadores veiculares).
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {historicoUnificado.map((grupo, idx) => (
                <div key={`${grupo.titulo}-${grupo.subtitulo}-${idx}`} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold">{grupo.titulo}</h4>
                        {grupo.tipoProduto && (
                          <Badge variant="outline" className="text-xs">
                            {grupo.tipoProduto === 'fotovoltaico' ? 'Fotovoltaico' : 'Carregador Veicular'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{grupo.subtitulo}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {grupo.pagamentos.map((pagamento) => (
                      <div key={pagamento.id} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{formatDate(pagamento.data_pagamento)}</span>
                          </div>
                          <Badge variant="outline">
                            {FORMAS_PAGAMENTO.find(f => f.value === pagamento.forma_pagamento)?.label || pagamento.forma_pagamento}
                          </Badge>
                          {pagamento.origem && (
                            <Badge variant={pagamento.origem === 'comissao' ? 'secondary' : 'default'} className="text-[10px] h-5">
                              {pagamento.origem === 'comissao' ? 'Comissão' : 'Contas a Pagar'}
                            </Badge>
                          )}
                          <span className="text-sm text-muted-foreground">
                            Comissão
                          </span>
                          {pagamento.observacoes && (
                            <span className="text-sm text-muted-foreground italic max-w-xs truncate" title={pagamento.observacoes}>
                              {pagamento.observacoes}
                            </span>
                          )}
                          {pagamento.comprovante_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(pagamento.comprovante_url, '_blank')}
                              className="h-7"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              {pagamento.comprovante_nome_arquivo || 'Ver Comprovante'}
                            </Button>
                          )}
                        </div>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(pagamento.valor_pago)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

